import React from "react";
import {
  Box,
  Divider,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import { CloseIcon, DeleteIcon } from "@chakra-ui/icons";
import { useTheme } from "next-themes";

import { Badge } from "@/components/ui/badge";
import { Field, FieldLabel } from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatNotificationDetailKey,
  formatNotificationTypeLabel,
  NOTIFICATION_DETAIL_BODY_CLASS,
  NOTIFICATION_DETAIL_FIELD_LABEL_CLASS,
  NOTIFICATION_DETAIL_MESSAGE_CLASS,
  NOTIFICATION_DETAIL_TITLE_PAGE_CLASS,
  NOTIFICATION_DETAIL_TITLE_PANE_CLASS,
  notificationTypeBadgeClass,
} from "@/constants/notificationDetailChrome";
import { NOTIFICATION_AURA_NEW_BEST_DAY_SRC } from "@/constants/notificationAuraImagery";
import { getReportReadyNotificationImageSrc } from "@/constants/notificationReportReadyImagery";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { NOTIFICATION_DETAIL_SUPPRESSED_KEYS } from "@/utils/notificationDetailNavigation";
import {
  buildNotificationDetailActions,
  stripNotificationSuggestedNextStepsMarkdown,
} from "@/utils/notificationDetailActions";
import {
  buildBillingDetailActions,
  type BillingDetailActionKind,
} from "@/utils/notificationBillingDeepLinks";
import {
  buildBrokerDisconnectedDetailActions,
  buildBrokerReconnectedDetailActions,
  buildSyncFailedDetailActions,
  buildSyncRecoveredDetailActions,
} from "@/utils/notificationBrokerDisconnectedDeepLinks";
import { buildRecordTradingDayDetailActions } from "@/utils/notificationRecordDayDeepLinks";
import { buildReportReadyDetailActions, buildReportFailedDetailActions } from "@/utils/notificationReportReadyDeepLinks";
import {
  buildAnnouncementDetailActions,
  isAnnouncementDetailEventKey,
} from "@/utils/notificationAnnouncementDeepLinks";
import PerformanceSummaryMetricsGrid from "./PerformanceSummaryMetricsGrid";
import { NotificationDetailActionsPanel } from "./NotificationDetailActionsPanel";
import { NotificationDetailDeleteButton } from "./NotificationDetailDeleteButton";
import { NotificationDetailValueCell } from "./NotificationDetailValueCell";
import {
  formatBillingPaymentActionRequiredMessageHtml,
  formatBillingPaymentFailedMessageHtml,
  formatBillingPaymentSucceededMessageHtml,
  formatBillingSubscriptionCancelledMessageHtml,
  formatBillingSubscriptionUpdatedMessageHtml,
  formatBrokerDisconnectedMessageHtml,
  formatBrokerReconnectedMessageHtml,
  formatNewBestTradingDayMessageHtml,
  formatNewWorstTradingDayMessageHtml,
  formatPnlMilestoneCrossedMessageHtml,
  formatReportFailedMessageHtml,
  formatReportReadyMessageHtml,
  formatSyncFailedMessageHtml,
  formatSyncRecoveredMessageHtml,
  isActiveBillingEventKey,
  isBillingPaymentActionRequiredEventKey,
  isBillingPaymentFailedEventKey,
  isBillingPaymentSucceededEventKey,
  isBillingSubscriptionCancelledEventKey,
  isBillingSubscriptionUpdatedEventKey,
  isBrokerDisconnectedEventKey,
  isBrokerReconnectedEventKey,
  isNewBestTradingDayEventKey,
  isNewWorstTradingDayEventKey,
  isPerformanceSummaryEventKey,
  isPnlMilestoneCrossedEventKey,
  isRecordTradingDayEventKey,
  isReportDetailEventKey,
  isReportFailedEventKey,
  isReportReadyEventKey,
  isSyncFailedEventKey,
  isSyncRecoveredEventKey,
  isSystemBrokerDetailEventKey,
} from "../../utils/notificationDisplay";
import type { NotificationModel } from "./notificationModels";
import { getNotificationIcon } from "./notificationModels";

export interface NotificationDetailPanelProps {
  notification: NotificationModel;
  onDelete?: (id: string) => void;
  onClose?: () => void;
  /** When true, show the close button (mobile detail pane behavior). */
  showCloseButton?: boolean;
  /** Layout: side pane (NotificationModal) vs preview card (settings) vs full page (notifications centre). */
  variant?: "sidePane" | "full" | "page";
}

export default function NotificationDetailPanel({
  notification,
  onDelete,
  onClose,
  showCloseButton = true,
  variant = "sidePane",
}: NotificationDetailPanelProps) {
  const isMobile = useIsMobile();
  const isPage = variant === "page";
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const modalBg = isDark ? "var(--sidebar)" : "white";
  const borderColor = isDark ? "var(--border)" : "gray.200";

  const typeLabel = formatNotificationTypeLabel(notification.type);
  const isPerfSummary = isPerformanceSummaryEventKey(notification.eventKey);
  const isNewBestDay = isNewBestTradingDayEventKey(notification.eventKey);
  const isNewWorstDay = isNewWorstTradingDayEventKey(notification.eventKey);
  const isRecordDay = isRecordTradingDayEventKey(notification.eventKey);
  const isMilestone = isPnlMilestoneCrossedEventKey(notification.eventKey);
  const isBrokerDisconnected = isBrokerDisconnectedEventKey(notification.eventKey);
  const isBrokerReconnected = isBrokerReconnectedEventKey(notification.eventKey);
  const isSyncFailed = isSyncFailedEventKey(notification.eventKey);
  const isSyncRecovered = isSyncRecoveredEventKey(notification.eventKey);
  const isSystemBrokerDetail = isSystemBrokerDetailEventKey(notification.eventKey);
  const isBillingDetail = isActiveBillingEventKey(notification.eventKey);
  const isReportReady = isReportReadyEventKey(notification.eventKey);
  const isReportFailed = isReportFailedEventKey(notification.eventKey);
  const isReportDetail = isReportDetailEventKey(notification.eventKey);
  const isAnnouncementDetail = isAnnouncementDetailEventKey(
    notification.eventKey,
    notification.type
  );
  const reportReadyImageSrc = isReportReady
    ? getReportReadyNotificationImageSrc(notification.details)
    : null;
  const isMessageOnlyDetail =
    isRecordDay ||
    isMilestone ||
    isSystemBrokerDetail ||
    isBillingDetail ||
    isReportDetail ||
    isAnnouncementDetail;
  /** CTAs rendered under the message (not header toolbar). */
  const showUnderMessageActions =
    isRecordDay ||
    isSystemBrokerDetail ||
    isBillingDetail ||
    isReportDetail ||
    isAnnouncementDetail;

  const billingActionKind = React.useMemo((): BillingDetailActionKind | null => {
    if (isBillingPaymentSucceededEventKey(notification.eventKey)) {
      return "payment_succeeded";
    }
    if (isBillingPaymentFailedEventKey(notification.eventKey)) {
      return "payment_failed";
    }
    if (isBillingPaymentActionRequiredEventKey(notification.eventKey)) {
      return "payment_action_required";
    }
    if (isBillingSubscriptionCancelledEventKey(notification.eventKey)) {
      return "subscription_cancelled";
    }
    if (isBillingSubscriptionUpdatedEventKey(notification.eventKey)) {
      return "subscription_updated";
    }
    return null;
  }, [notification.eventKey]);

  const detailActions = React.useMemo(() => {
    if (isRecordDay) {
      return buildRecordTradingDayDetailActions(notification.details, {
        includeTradeReview: isNewWorstDay,
      });
    }
    if (isBrokerDisconnected) {
      return buildBrokerDisconnectedDetailActions(notification.details);
    }
    if (isBrokerReconnected) {
      return buildBrokerReconnectedDetailActions(notification.details);
    }
    if (isSyncFailed) {
      return buildSyncFailedDetailActions(notification.details);
    }
    if (isSyncRecovered) {
      return buildSyncRecoveredDetailActions(notification.details);
    }
    if (billingActionKind) {
      return buildBillingDetailActions(notification.details, billingActionKind);
    }
    if (isReportReady) {
      return buildReportReadyDetailActions(notification.details);
    }
    if (isReportFailed) {
      return buildReportFailedDetailActions(notification.details);
    }
    if (isAnnouncementDetail) {
      return buildAnnouncementDetailActions(notification.details);
    }
    // Milestone is message-only — no stats/report CTAs even if payload URLs exist.
    if (isMilestone) {
      return [];
    }
    return buildNotificationDetailActions(notification.details);
  }, [
    billingActionKind,
    isAnnouncementDetail,
    isBrokerDisconnected,
    isBrokerReconnected,
    isMilestone,
    isNewWorstDay,
    isRecordDay,
    isReportFailed,
    isReportReady,
    isSyncFailed,
    isSyncRecovered,
    notification.details,
  ]);

  const messageHtml = React.useMemo(() => {
    if (isPerfSummary) return "";
    if (isNewBestDay) {
      const composed = formatNewBestTradingDayMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isNewWorstDay) {
      const composed = formatNewWorstTradingDayMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isMilestone) {
      const composed = formatPnlMilestoneCrossedMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isBrokerDisconnected) {
      const composed = formatBrokerDisconnectedMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isBrokerReconnected) {
      const composed = formatBrokerReconnectedMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isSyncFailed) {
      const composed = formatSyncFailedMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isSyncRecovered) {
      const composed = formatSyncRecoveredMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isBillingPaymentSucceededEventKey(notification.eventKey)) {
      const composed = formatBillingPaymentSucceededMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isBillingPaymentFailedEventKey(notification.eventKey)) {
      const composed = formatBillingPaymentFailedMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isBillingPaymentActionRequiredEventKey(notification.eventKey)) {
      const composed = formatBillingPaymentActionRequiredMessageHtml(
        notification.details
      );
      if (composed) return composed;
    }
    if (isBillingSubscriptionCancelledEventKey(notification.eventKey)) {
      const composed = formatBillingSubscriptionCancelledMessageHtml(
        notification.details
      );
      if (composed) return composed;
    }
    if (isBillingSubscriptionUpdatedEventKey(notification.eventKey)) {
      const composed = formatBillingSubscriptionUpdatedMessageHtml(
        notification.details
      );
      if (composed) return composed;
    }
    if (isReportReady) {
      const composed = formatReportReadyMessageHtml(notification.details);
      if (composed) return composed;
    }
    if (isReportFailed) {
      const composed = formatReportFailedMessageHtml(notification.details);
      if (composed) return composed;
    }
    const stripped = stripNotificationSuggestedNextStepsMarkdown(notification.message || "");
    return stripped;
  }, [
    isBrokerDisconnected,
    isBrokerReconnected,
    isMilestone,
    isNewBestDay,
    isNewWorstDay,
    isPerfSummary,
    isReportFailed,
    isReportReady,
    isSyncFailed,
    isSyncRecovered,
    notification.details,
    notification.eventKey,
    notification.message,
  ]);

  const detailEntries = React.useMemo(() => {
    if (!notification.details || typeof notification.details !== "object") {
      return [];
    }
    return Object.entries(notification.details as Record<string, unknown>)
      .filter(([key]) => !NOTIFICATION_DETAIL_SUPPRESSED_KEYS.has(key))
      .slice(0, 12);
  }, [notification.details]);

  const showDetailsTable =
    detailEntries.length > 0 &&
    !isAnnouncementDetail &&
    !(notification.type === "performance" && isPerfSummary) &&
    !isMessageOnlyDetail;

  const showPerformanceSummary =
    isPerfSummary &&
    notification.details &&
    typeof notification.details === "object";

  const showMessageField = showPerformanceSummary || Boolean(messageHtml);
  const showNextSteps = detailActions.length > 0;
  /** Record-day / broker-disconnected CTAs sit under the message, not in the header toolbar. */
  const showHeaderActions = showNextSteps && !showUnderMessageActions;
  const actionLayout = isMobile ? "stack" : "row";

  const pageDeleteButton =
    isPage && onDelete ? (
      <NotificationDetailDeleteButton onClick={() => onDelete(notification.id)} />
    ) : null;

  const pageActionButtons =
    isPage && showHeaderActions ? (
      <NotificationDetailActionsPanel
        actions={detailActions}
        layout={actionLayout}
        className={isMobile ? "mt-3" : "justify-end"}
      />
    ) : null;

  const pageDesktopToolbar =
    isPage && !isMobile && (showHeaderActions || onDelete) ? (
      <div
        data-notification-detail-toolbar=""
        className="flex max-w-full shrink-0 flex-row flex-wrap items-center justify-end gap-2"
      >
        {pageActionButtons}
        {pageDeleteButton}
      </div>
    ) : null;

  const typeBadge = (
    <Badge
      variant="outline"
      data-notification-type-badge=""
      className={cn(notificationTypeBadgeClass(notification.type), "shrink-0")}
    >
      {typeLabel}
    </Badge>
  );

  const pageTitleBlock = (
    <Box minW={0} flex={isPage && !isMobile ? "1" : undefined} w="100%">
      <Flex align="center" gap={3} flexWrap="wrap" minW={0}>
        {typeBadge}
        <h2
          className={cn(
            "m-0 min-w-0 max-w-full flex-1 break-words",
            NOTIFICATION_DETAIL_TITLE_PAGE_CLASS
          )}
        >
          {notification.title}
        </h2>
      </Flex>
      {isPage && isMobile ? pageActionButtons : null}
    </Box>
  );

  return (
    <Box
      w={variant === "sidePane" ? { base: "100%", md: "50%" } : "100%"}
      minW={0}
      h={isPage && !isMobile ? "100%" : undefined}
      flex={isPage && !isMobile ? "1" : undefined}
      minH={isPage && !isMobile ? 0 : undefined}
      p={variant === "full" || isPage ? { base: 4, md: 6 } : 6}
      bg={isPage ? "transparent" : modalBg}
      overflowY={isPage && !isMobile ? "auto" : undefined}
      maxH={
        isPage
          ? undefined
          : variant === "full"
            ? { base: "70vh", md: "70vh" }
            : { base: "calc(100vh - 120px)", md: "calc(90vh - 100px)" }
      }
      data-notification-detail-panel=""
      data-notification-detail-variant={variant}
    >
      {isPage ? (
        isMobile ? (
          <Box position="relative" mb={4} w="100%">
            <Flex align="flex-start" justify="space-between" gap={3} w="100%">
              <Flex direction="column" align="flex-start" gap={1} minW={0} flex="1">
                <Box flexShrink={0} lineHeight={0}>
                  {typeBadge}
                </Box>
                <h2
                  className={cn(
                    "m-0 min-w-0 max-w-full break-words",
                    NOTIFICATION_DETAIL_TITLE_PAGE_CLASS
                  )}
                >
                  {notification.title}
                </h2>
              </Flex>
              {pageDeleteButton ? (
                <Box flexShrink={0} lineHeight={0}>
                  {pageDeleteButton}
                </Box>
              ) : null}
            </Flex>
            {pageActionButtons}
          </Box>
        ) : (
          <Flex
            direction="row"
            align="flex-start"
            justify="space-between"
            gap={4}
            mb={4}
            w="100%"
          >
            {pageTitleBlock}
            {pageDesktopToolbar}
          </Flex>
        )
      ) : isMobile ? (
        <Flex align="flex-start" justify="space-between" gap={3} mb={4} w="100%">
          <Flex direction="column" align="flex-start" gap={1} minW={0} flex="1">
            <Box flexShrink={0} lineHeight={0}>
              {typeBadge}
            </Box>
            <h2
              className={cn(
                "m-0 min-w-0 max-w-full",
                NOTIFICATION_DETAIL_TITLE_PANE_CLASS
              )}
            >
              {notification.title}
            </h2>
            {showHeaderActions ? (
              <NotificationDetailActionsPanel
                actions={detailActions}
                className="mt-3"
              />
            ) : null}
          </Flex>
          <HStack
            spacing={2}
            flexShrink={0}
            alignItems="flex-start"
            justifyContent="flex-end"
          >
            {onDelete ? (
              <IconButton
                aria-label="Delete notification"
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => onDelete(notification.id)}
              />
            ) : null}
            {showCloseButton && onClose ? (
              <IconButton
                aria-label="Close details"
                icon={<CloseIcon />}
                size="sm"
                variant="ghost"
                onClick={onClose}
              />
            ) : null}
          </HStack>
        </Flex>
      ) : (
        <Flex justifyContent="space-between" alignItems="flex-start" mb={4} gap={3}>
          <HStack spacing={3} minW={0} align="flex-start">
            <Box fontSize="24px" flexShrink={0} pt={0.5} aria-hidden>
              {getNotificationIcon(notification.type)}
            </Box>
            <Box minW={0}>
              <Flex align="center" gap={3} flexWrap="wrap" minW={0}>
                {typeBadge}
                <h2
                  className={cn(
                    "m-0 min-w-0 max-w-full flex-1",
                    NOTIFICATION_DETAIL_TITLE_PANE_CLASS
                  )}
                >
                  {notification.title}
                </h2>
              </Flex>
              {showHeaderActions ? (
                <NotificationDetailActionsPanel
                  actions={detailActions}
                  className="mt-3"
                />
              ) : null}
            </Box>
          </HStack>
          <HStack
            spacing={2}
            flexShrink={0}
            alignSelf="flex-start"
            alignItems="center"
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            {onDelete ? (
              <IconButton
                aria-label="Delete notification"
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => onDelete(notification.id)}
              />
            ) : null}
          </HStack>
        </Flex>
      )}

      {isPage ? (
        <Separator
          decorative
          className="mb-4 h-0 w-full shrink-0 border-0 border-t border-solid border-border bg-transparent p-0 shadow-none"
        />
      ) : (
        <Divider mb={4} borderColor={borderColor} />
      )}

      <VStack spacing={5} align="stretch">
        {showMessageField || isMessageOnlyDetail ? (
          <Field className="w-full gap-2">
            {showMessageField && isPerfSummary ? (
              <FieldLabel className={NOTIFICATION_DETAIL_FIELD_LABEL_CLASS}>
                Performance summary
              </FieldLabel>
            ) : null}
            {showPerformanceSummary ? (
              <PerformanceSummaryMetricsGrid data={notification.details} />
            ) : isMessageOnlyDetail ? (
              <Flex direction="column" align="stretch" gap={4} w="100%">
                {isRecordDay ? (
                  <h3
                    className={cn(
                      "m-0 min-w-0 max-w-full break-words",
                      isPage
                        ? NOTIFICATION_DETAIL_TITLE_PAGE_CLASS
                        : NOTIFICATION_DETAIL_TITLE_PANE_CLASS
                    )}
                  >
                    {isNewBestDay ? "Congratulations!" : "Hang in there."}
                  </h3>
                ) : isMilestone ? (
                  <h3
                    className={cn(
                      "m-0 min-w-0 max-w-full break-words",
                      isPage
                        ? NOTIFICATION_DETAIL_TITLE_PAGE_CLASS
                        : NOTIFICATION_DETAIL_TITLE_PANE_CLASS
                    )}
                  >
                    Nice work.
                  </h3>
                ) : null}
                {messageHtml ? (
                  <Box
                    className={NOTIFICATION_DETAIL_MESSAGE_CLASS}
                    w="100%"
                    dangerouslySetInnerHTML={{
                      __html: messageHtml,
                    }}
                  />
                ) : null}
                {reportReadyImageSrc ? (
                  <Box
                    w="100%"
                    display="flex"
                    justifyContent={{ base: "center", md: "flex-start" }}
                  >
                    <Box
                      as="img"
                      src={reportReadyImageSrc}
                      alt=""
                      role="presentation"
                      w={{ base: "100%", md: "auto" }}
                      maxW={{
                        base: "100%",
                        md: isPage ? "420px" : "360px",
                        lg: isPage ? "480px" : "400px",
                      }}
                      h="auto"
                      borderRadius="12px"
                      objectFit="contain"
                    />
                  </Box>
                ) : null}
                {showUnderMessageActions && showNextSteps ? (
                  <NotificationDetailActionsPanel
                    actions={detailActions}
                    layout="row"
                    className="w-full"
                  />
                ) : null}
                {isNewBestDay ? (
                  <Box
                    w="100%"
                    display="flex"
                    justifyContent={{ base: "center", md: "flex-start" }}
                  >
                    <Box
                      as="img"
                      src={NOTIFICATION_AURA_NEW_BEST_DAY_SRC}
                      alt=""
                      role="presentation"
                      // Mobile: full message-column width (same L/R edges as copy).
                      // Larger: fixed max, left-aligned under the message.
                      w={{ base: "100%", md: "auto" }}
                      maxW={{
                        base: "100%",
                        md: isPage ? "320px" : "280px",
                        lg: isPage ? "360px" : "300px",
                      }}
                      h="auto"
                      borderRadius="12px"
                      objectFit="contain"
                    />
                  </Box>
                ) : null}
              </Flex>
            ) : messageHtml ? (
              <Box
                className={NOTIFICATION_DETAIL_MESSAGE_CLASS}
                dangerouslySetInnerHTML={{
                  __html: messageHtml,
                }}
              />
            ) : null}
          </Field>
        ) : null}

        {!isPage ? (
          <Field className="w-full gap-2">
            <FieldLabel className={NOTIFICATION_DETAIL_FIELD_LABEL_CLASS}>Time</FieldLabel>
            <p className={cn("m-0", NOTIFICATION_DETAIL_BODY_CLASS)}>{notification.time}</p>
          </Field>
        ) : null}

        {showDetailsTable ? (
            <Field className="w-full gap-2">
              {!isPage ? (
                <FieldLabel className={NOTIFICATION_DETAIL_FIELD_LABEL_CLASS}>
                  Details
                </FieldLabel>
              ) : null}
              <div
                data-notification-details-table=""
                data-shadcn-data-table=""
                className="max-w-full overflow-x-auto rounded-[6px] border border-border bg-background dark:bg-transparent"
              >
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead
                        className={cn(
                          NOTIFICATION_DETAIL_FIELD_LABEL_CLASS,
                          "h-10 min-w-[140px] whitespace-normal"
                        )}
                      >
                        Metric
                      </TableHead>
                      <TableHead
                        className={cn(
                          NOTIFICATION_DETAIL_FIELD_LABEL_CLASS,
                          "h-10 whitespace-normal"
                        )}
                      >
                        Value
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailEntries.map(([key, value]) => (
                      <TableRow key={key} className="hover:bg-muted/50">
                        <TableCell className="min-w-[140px] align-top whitespace-normal">
                          <span className={NOTIFICATION_DETAIL_FIELD_LABEL_CLASS}>
                            {formatNotificationDetailKey(key)}
                          </span>
                        </TableCell>
                        <TableCell className="align-top whitespace-normal">
                          <NotificationDetailValueCell fieldKey={key} value={value} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Field>
          ) : null}

        {/* Avatar block (kept for parity with modal layout even when not used) */}
        <Box display="none">
          <Text fontSize="sm">{notification.avatarName}</Text>
        </Box>
      </VStack>
    </Box>
  );
}
